/**
 * The secret link, and what may be done to it.
 *
 * Clients copy it and pass it on; staff can rotate it — which kills every copy at once — or
 * delete the report altogether. Both are irreversible, so both ask first.
 */
import { useRef, useState } from 'react';
import { Button, Card, Code, Group, Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { useDeleteReport, useRotateReportSecret, type Report } from '@/api/reports';
import { useFormat } from '@/components/useFormat';
import { STATUS_COLORS } from '@/theme/colors';
import { brand } from '@/theme/theme';

/** Copies `text`, falling back to selecting it where the clipboard is not ours to write. */
export async function copyText(text: string, node: HTMLElement | null): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    if (!node) return false;
    const range = document.createRange();
    range.selectNodeContents(node);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    return false;
  }
}

export interface ConfirmModalProps {
  opened: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/** A yes/no dialog. Mantine's modal manager is not a dependency of this app, so this is it. */
export function ConfirmModal({ opened, title, description, confirmLabel, loading, onConfirm, onClose }: ConfirmModalProps) {
  const { t } = useTranslation('reports');
  return (
    <Modal opened={opened} onClose={onClose} title={title}>
      <Stack gap="md">
        <Text fz={14} c={brand.textMuted}>
          {description}
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('share.cancel')}
          </Button>
          <Button color="red" onClick={onConfirm} loading={loading}>
            {confirmLabel ?? t('share.confirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export interface ShareLinkCardProps {
  report: Report;
  /** Staff may rotate the secret and delete the report (RP14). */
  staff?: boolean;
  onDeleted?: () => void;
}

export function ShareLinkCard({ report, staff = false, onDeleted }: ShareLinkCardProps) {
  const { t } = useTranslation('reports');
  const { relative } = useFormat();
  const linkRef = useRef<HTMLElement>(null);
  const [confirm, setConfirm] = useState<'rotate' | 'delete' | null>(null);
  const rotate = useRotateReportSecret();
  const remove = useDeleteReport();
  // RP16: the secret is kept while the report is hidden, but the link resolves only once shown.
  const hidden = !report.visible_to_client;

  const copy = () => {
    void copyText(report.link, linkRef.current).then((copied) => {
      if (copied) notifications.show({ color: 'forest', message: t('share.copied') });
    });
  };

  const run = (mutate: () => Promise<unknown>, message: string) => {
    void mutate()
      .then(() => {
        setConfirm(null);
        notifications.show({ color: 'forest', message });
      })
      .catch((e) => notifications.show({ color: 'red', message: errorMessage(e) }));
  };

  return (
    <Card className="report-share">
      <Stack gap="sm">
        <Text fw={700}>{t('share.title')}</Text>
        <Code ref={linkRef} block style={{ wordBreak: 'break-all' }}>
          {report.link}
        </Code>
        <Text fz={12} c={brand.textMuted}>
          {t('share.hint')}
        </Text>
        {hidden && (
          <Text
            fz={13}
            fw={600}
            px="sm"
            py={6}
            style={{ color: STATUS_COLORS.pending.color, backgroundColor: STATUS_COLORS.pending.bg, border: `1px solid ${STATUS_COLORS.pending.border}`, borderRadius: 8 }}
          >
            {t('share.hiddenHint')}
          </Text>
        )}
        <Group gap="xs" justify="space-between">
          <Text fz={12} c={brand.textMuted}>
            {t('share.views', { count: report.view_count ?? 0 })} ·{' '}
            {report.last_viewed_at ? t('share.lastViewed', { when: relative(report.last_viewed_at) }) : t('share.never')}
          </Text>
          <Group gap="xs">
            <Button size="xs" variant="default" onClick={copy} disabled={hidden}>
              {t('share.copy')}
            </Button>
            {staff && (
              <>
                <Button size="xs" variant="default" onClick={() => setConfirm('rotate')}>
                  {t('share.rotate')}
                </Button>
                <Button size="xs" variant="subtle" color="red" onClick={() => setConfirm('delete')}>
                  {t('share.delete')}
                </Button>
              </>
            )}
          </Group>
        </Group>
      </Stack>

      <ConfirmModal
        opened={confirm === 'rotate'}
        title={t('share.rotateTitle')}
        description={t('share.rotateHint')}
        confirmLabel={t('share.rotate')}
        loading={rotate.isPending}
        onConfirm={() => run(() => rotate.mutateAsync(report.public_id), t('share.rotated'))}
        onClose={() => setConfirm(null)}
      />
      <ConfirmModal
        opened={confirm === 'delete'}
        title={t('share.deleteTitle')}
        description={t('share.deleteHint')}
        confirmLabel={t('share.delete')}
        loading={remove.isPending}
        onConfirm={() =>
          run(
            () => remove.mutateAsync(report.public_id).then(() => onDeleted?.()),
            t('share.deleted'),
          )
        }
        onClose={() => setConfirm(null)}
      />
    </Card>
  );
}
